import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import { rimsList, rimsUpsert, rimsDelete, fmtMoney } from "@/lib/api";
import {
  ActiveBadge,
  DeleteConfirm,
  TextField,
  NumberField,
  SwitchField,
  slugify,
} from "./RimsKit";

const EMPTY = {
  key: "",
  name: "",
  years: "",
  monthly_rate: "",
  order: null,
  active: true,
};

export const TiersManager = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mode, setMode] = useState("create");
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingKey, setTogglingKey] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems((await rimsList(token, "tiers")) || []);
    } catch {
      toast.error("Failed to load experience tiers");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const openCreate = () => {
    setMode("create");
    setForm({ ...EMPTY });
    setDialogOpen(true);
  };
  const openEdit = (item) => {
    setMode("edit");
    setForm({ ...EMPTY, ...item });
    setDialogOpen(true);
  };

  const handleToggleActive = async (item) => {
    setTogglingKey(item.key);
    setItems((prev) =>
      prev.map((r) => (r.key === item.key ? { ...r, active: !r.active } : r))
    );
    try {
      await rimsUpsert(token, "tiers", item.key, { ...item, active: !item.active });
      toast.success(`${item.name} ${!item.active ? "enabled" : "disabled"}`);
    } catch {
      toast.error("Failed to update tier");
      load();
    } finally {
      setTogglingKey("");
    }
  };

  const handleSave = async () => {
    const key = form.key?.trim() || slugify(form.name);
    if (!form.name?.trim()) return toast.error("Name is required");
    if (!key) return toast.error("Key is required");
    if (mode === "create" && items.some((r) => r.key === key)) {
      return toast.error(`Key "${key}" already exists`);
    }
    setSaving(true);
    try {
      const payload = { ...form, key };
      if (payload.monthly_rate === "") payload.monthly_rate = null;
      if (payload.order === "") payload.order = null;
      await rimsUpsert(token, "tiers", key, payload);
      toast.success(mode === "edit" ? "Tier updated" : "Tier created");
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save tier");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await rimsDelete(token, "tiers", deleteTarget.key);
      toast.success("Tier deleted");
      setDeleteTarget(null);
      load();
    } catch {
      toast.error("Failed to delete tier");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid="rims-tiers-manager">
      <div className="flex justify-between items-center mb-5">
        <p className="text-white/50 text-sm">
          Experience &amp; rate reference matrix. Edit tier names, experience ranges, and monthly rates.
        </p>
        <Button
          data-testid="rims-add-tier-button"
          onClick={openCreate}
          className="bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-lg h-10"
        >
          <Plus size={16} className="mr-1" /> Add Tier
        </Button>
      </div>

      <div className="rounded-xl border border-white/10 overflow-hidden" style={{ backgroundColor: "#0F1424" }}>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-[#1E6BFF]" size={26} />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-white/50" data-testid="rims-tiers-empty">
            No experience tiers found.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-white/60 w-16">Active</TableHead>
                  <TableHead className="text-white/60">Tier</TableHead>
                  <TableHead className="text-white/60">Experience</TableHead>
                  <TableHead className="text-white/60">Monthly Rate</TableHead>
                  <TableHead className="text-white/60 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => (
                  <TableRow key={r.key} data-testid="rims-tier-row" className="border-white/10 hover:bg-white/5">
                    <TableCell>
                      <Switch
                        data-testid={`rims-tier-toggle-${r.key}`}
                        checked={!!r.active}
                        disabled={togglingKey === r.key}
                        onCheckedChange={() => handleToggleActive(r)}
                        className="data-[state=checked]:bg-[#2EE59D]"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-white flex items-center gap-2">
                        {r.name} <ActiveBadge active={r.active} />
                      </div>
                      <div className="text-white/40 text-xs">{r.key}</div>
                    </TableCell>
                    <TableCell className="text-white/70">{r.years || "-"}</TableCell>
                    <TableCell className="text-white font-medium tabular-nums">
                      {r.monthly_rate != null ? `${fmtMoney(r.monthly_rate)}/mo` : "-"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          data-testid={`rims-edit-tier-${r.key}`}
                          onClick={() => openEdit(r)}
                          className="h-8 w-8 text-white/60 hover:text-white hover:bg-white/10"
                        >
                          <Pencil size={15} />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          data-testid={`rims-delete-tier-${r.key}`}
                          onClick={() => setDeleteTarget(r)}
                          className="h-8 w-8 text-white/60 hover:text-[#FF5C7A] hover:bg-white/10"
                        >
                          <Trash2 size={15} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      <p className="text-white/40 text-xs mt-3">{items.length} tier(s)</p>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-[#0F1424] border-white/10 text-white" data-testid="rims-tier-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              {mode === "edit" ? "Edit Tier" : "Add Tier"}
            </DialogTitle>
            <DialogDescription className="text-white/45">
              Set the tier name, experience range, and monthly rate used in the rate reference matrix.
            </DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 mt-1">
            <TextField label="Tier Name *" testid="rims-tier-name-input" value={form.name} onChange={(v) => set({ name: v })} />
            <TextField
              label="Key *"
              testid="rims-tier-key-input"
              value={form.key}
              disabled={mode === "edit"}
              hint={mode === "edit" ? "Key cannot be changed." : "Auto-generated if blank."}
              onChange={(v) => set({ key: slugify(v) })}
            />
            <TextField
              label="Experience Range"
              testid="rims-tier-years-input"
              value={form.years}
              hint='e.g. "2-4 Years"'
              onChange={(v) => set({ years: v })}
            />
            <NumberField
              label="Monthly Rate (USD)"
              testid="rims-tier-rate-input"
              value={form.monthly_rate}
              onChange={(v) => set({ monthly_rate: v })}
            />
            <NumberField label="Display Order" value={form.order ?? ""} onChange={(v) => set({ order: v === "" ? null : v })} />
          </div>
          <SwitchField
            label="Active"
            testid="rims-tier-active-switch"
            checked={form.active}
            onChange={(v) => set({ active: v })}
          />
          <DialogFooter className="mt-2">
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-white/70 hover:text-white hover:bg-white/5">
              Cancel
            </Button>
            <Button
              data-testid="rims-tier-save-button"
              onClick={handleSave}
              disabled={saving}
              className="bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-lg"
            >
              {saving ? <Loader2 className="animate-spin mr-1" size={15} /> : null}
              {mode === "edit" ? "Save Changes" : "Create Tier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteConfirm
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete tier?"
        description={`This will permanently remove the "${deleteTarget?.name}" experience tier.`}
        onConfirm={handleDelete}
        busy={deleting}
        testid="rims-delete-tier-dialog"
      />
    </div>
  );
};
